type WaitForOptions =
  | {
      maxTries: number;
      exponentialTimeout: number;
    }
  | {
      maxTries: number;
      fixedTimeout: number;
    };

/**
 * Waits for a condition function to return true by polling it periodically.
 *
 * Checks the condition immediately, then polls based on the specified timeout
 * strategy (fixed or exponential backoff) until the condition returns true
 * or the maximum number of tries is exceeded.
 *
 * @param condition - A function that returns a boolean. Polling stops when this function returns true.
 * @param [options={ maxTries: 100, fixedTimeout: 0 }] - Optional configuration for polling behavior.
 * - `maxTries`: Maximum number of polling attempts (default: 100).
 * - `fixedTimeout`: Fixed delay (ms) between attempts (default: 0). Used only if `exponentialTimeout` is not set.
 * - `exponentialTimeout`: Initial delay (ms) for exponential backoff. If set, delay doubles after each failed attempt.
 * @returns A promise that resolves to `true` if the condition becomes true within the allowed tries,
 * or `false` if the condition remains false after `maxTries` attempts.
 *
 * @example
 * ```ts
 * // Wait for an element to appear in the DOM using fixed polling
 * const elementAppeared = await waitFor(() => !!document.getElementById('myDynamicElement'), {
 *   maxTries: 50, // Try 50 times
 *   fixedTimeout: 200 // Wait 200ms between tries
 * });
 * if (elementAppeared) {
 *   console.log('Element found!');
 * } else {
 *   console.log('Timed out waiting for element.');
 * }
 * ```
 *
 * @example
 * ```ts
 * // Wait for a resource to be ready using exponential backoff
 * const resourceReady = await waitFor(() => checkResourceStatus() === 'ready', {
 *   maxTries: 10,
 *   exponentialTimeout: 100 // Start with 100ms, then 200ms, 400ms, etc.
 * });
 * console.log(`Resource ready status: ${resourceReady}`);
 * ```
 */
export function waitFor(
  condition: () => boolean,
  options: WaitForOptions = { maxTries: 100, fixedTimeout: 0 },
): Promise<boolean> {
  const check = async (): Promise<boolean> => {
    let iteration = 0;
    let timeout =
      "exponentialTimeout" in options
        ? options.exponentialTimeout
        : options.fixedTimeout;

    // first check
    let result = condition();

    // subsequent checks
    while (!result && iteration < options.maxTries) {
      // wait for the next check
      await new Promise((resolve) => setTimeout(resolve, timeout));

      // check the condition
      result = condition();

      // increase timeout for the next check
      if ("exponentialTimeout" in options) {
        timeout *= 2;
      }
      iteration++;
    }
    return result;
  };

  return check();
}
